import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLegalDoc } from '../legal/content';
import { useLanguage } from '../i18n/LanguageContext';

export default function LegalDocumentScreen({ type: propType, onBack }) {
  const { t, lang, toggleLang } = useLanguage();
  const params = useParams();
  const navigate = useNavigate();
  const docType = propType || params.type || 'privacy';
  const doc = getLegalDoc(docType, lang);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [docType]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="app legal-document-screen" style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      {/* Native In-App Top Bar */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'rgba(255, 255, 255, 0.96)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid #e2e8f0',
          padding: 'calc(12px + var(--safe-top)) 16px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <button
          type="button"
          onClick={handleBack}
          aria-label={t('common.back')}
          style={{
            background: '#f1f5f9',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            color: '#0f172a',
            cursor: 'pointer',
          }}
        >
          ←
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a', lineHeight: '1.2' }}>
            {doc.title}
          </h1>
          <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
            {t('legal.lastUpdated') || (lang === 'te' ? 'చివరిగా నవీకరించబడింది' : 'Last updated')}: {doc.lastUpdated} · v{doc.version}
          </p>
        </div>
        <button
          type="button"
          onClick={toggleLang}
          aria-label="Switch language"
          style={{
            background: '#f1f5f9',
            border: '1px solid #cbd5e1',
            borderRadius: '20px',
            padding: '6px 10px',
            fontSize: '12px',
            fontWeight: '700',
            color: '#0f172a',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <span>🌐</span>
          <span>{lang === 'te' ? 'English' : 'తెలుగు'}</span>
        </button>
      </header>

      {/* Main Document Content */}
      <div
        style={{
          flex: 1,
          padding: '18px 16px calc(30px + var(--safe-bottom))',
          maxWidth: '680px',
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Intro Subtitle Banner */}
        <div
          style={{
            background: '#ffffff',
            border: '1.5px solid #e2e8f0',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
          }}
        >
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>
            {docType === 'privacy' ? '🛡️' : docType === 'terms' ? '📜' : '🚨'}
          </div>
          <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5', color: '#334155', fontWeight: '600' }}>
            {doc.subtitle}
          </p>
        </div>

        {/* Structured Sections */}
        {doc.sections.map((sec) => (
          <section
            key={sec.id}
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '16px',
              padding: '18px 16px',
              marginBottom: '16px',
              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)',
            }}
          >
            <h2 style={{ margin: '0 0 10px', fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
              {sec.title}
            </h2>

            {sec.content.map((p, idx) => (
              <p
                key={idx}
                style={{
                  margin: '0 0 8px',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: '#475569',
                }}
              >
                {p}
              </p>
            ))}

            {sec.table && (
              <div style={{ overflowX: 'auto', marginTop: '12px' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '12px',
                    textAlign: 'left',
                  }}
                >
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #cbd5e1' }}>
                      {sec.table.headers.map((h, i) => (
                        <th key={i} style={{ padding: '8px 10px', fontWeight: '700', color: '#1e293b' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sec.table.rows.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        {row.map((cell, j) => (
                          <td key={j} style={{ padding: '8px 10px', color: '#475569', verticalAlign: 'top' }}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))}

        {/* Footer Note */}
        <footer style={{ textAlign: 'center', marginTop: '24px', fontSize: '12px', color: '#94a3b8' }}>
          <p>© {new Date().getFullYear()} MyTownly Rides · {lang === 'te' ? 'పీలేరు, ఆంధ్రప్రదేశ్' : 'Piler, Andhra Pradesh'}</p>
        </footer>
      </div>
    </div>
  );
}
